import {dropbox_os} from '@dropbox/api-v2-client';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {DropboxClientContext} from 'App';
import {useContext, useEffect, useState} from 'react';

import {downloadAppScreenshot} from './api';
import {AppCover, AppHost} from './AppCover';
import styles from './Apps.module.css';

export const AppThumbnail = ({
  host,
  name,
  screenshot,
  siteId,
  variant = 'feed',
}: {
  host?: AppHost;
  name: string;
  screenshot?: dropbox_os.PublishedAppScreenshot;
  siteId?: string;
  variant?: 'feed' | 'tile';
}) => {
  const client = useContext(DropboxClientContext);
  const sourceDeploymentId = screenshot?.source_deployment_id;
  const sha256 = screenshot?.sha256;
  const screenshotKey =
    siteId && sourceDeploymentId && sha256 ? `${siteId}:${sourceDeploymentId}:${sha256}` : null;
  const [thumbnailState, setThumbnailState] = useState<
    | {key: string; status: 'loading'}
    | {key: string; status: 'loaded'; url: string}
    | {key: string; status: 'failed'}
    | null
  >(null);

  useEffect(() => {
    if (!client || !siteId || !sourceDeploymentId || !sha256 || !screenshotKey) {
      return;
    }

    const controller = new AbortController();
    let objectUrl: string | undefined;

    setThumbnailState({key: screenshotKey, status: 'loading'});
    void downloadAppScreenshot(
      client,
      {
        site_id: siteId,
        source_deployment_id: sourceDeploymentId,
        sha256,
      },
      {signal: controller.signal}
    )
      .then((blob) => {
        if (controller.signal.aborted) {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setThumbnailState({key: screenshotKey, status: 'loaded', url: objectUrl});
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setThumbnailState({key: screenshotKey, status: 'failed'});
        }
      });

    return () => {
      controller.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [client, screenshotKey, sha256, siteId, sourceDeploymentId]);

  const currentThumbnailState = thumbnailState?.key === screenshotKey ? thumbnailState : null;
  const isDownloading = Boolean(
    client &&
    screenshotKey &&
    (!currentThumbnailState || currentThumbnailState.status === 'loading')
  );
  const loadedThumbnail = currentThumbnailState?.status === 'loaded' ? currentThumbnailState : null;
  const thumbnail = (
    <span className={variant === 'tile' ? styles.appTileThumbnail : styles.appThumbnail}>
      {loadedThumbnail ? (
        <img
          alt=""
          className={styles.appThumbnailImage}
          src={loadedThumbnail.url}
          loading="lazy"
          onError={() => setThumbnailState({key: loadedThumbnail.key, status: 'failed'})}
        />
      ) : isDownloading ? (
        <Skeleton.Box className={styles.appThumbnailSkeleton} />
      ) : (
        <AppCover host={host} name={name} variant={variant} />
      )}
    </span>
  );

  if (variant === 'tile') {
    return (
      <span className={styles.appTileThumbnailWrap} aria-hidden>
        {thumbnail}
      </span>
    );
  }

  return (
    <span className={styles.appThumbnailFeed} aria-hidden>
      {thumbnail}
    </span>
  );
};
